/**
 * V15 SFX 合成器 — 8 种音效，纯 Web Audio API 合成，零外部依赖
 *
 * 设计依据: design/v15-bgm-audio/README.md §6 音效 SFX 规划
 * Ardot 声音美学: 锐利瞬态 / 频谱边界清晰 / 克制的混响
 */

export type SfxId =
  | 'shoot'       // 射击 — 电子脉冲 + 金属共振尾音 0.15s
  | 'hit'         // 击中 — 低频闷响 + glitch 噪声 0.2s
  | 'scan'        // 扫描 — 上升电子 sweep + 回声 ping 0.3s
  | 'move'        // 移动 — 低频机械伺服声 0.1s
  | 'destroy'     // 摧毁 — glitch 中断 + 低频衰减 0.5s
  | 'message'     // 通信 — 数据 burst 脉冲 0.15s
  | 'ui_click'    // UI 交互 — 方波脉冲 0.05s
  | 'transition'; // 过渡动画 — 低频 whoosh 0.5s

interface SfxNode {
  stop: () => void;
}

const SYNTH_CACHE = new Map<SfxId, (ctx: AudioContext, dest: AudioNode) => SfxNode>();

// ============================================================
// Shoot — 电子脉冲 + 金属共振尾音 (0.15s)
// ============================================================
SYNTH_CACHE.set('shoot', (ctx, dest) => {
  const now = ctx.currentTime;
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.4, now);
  masterGain.connect(dest);

  // 主脉冲：锯齿波快速下降
  const osc = ctx.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(800, now);
  osc.frequency.exponentialRampToValueAtTime(120, now + 0.08);
  
  const oscFilter = ctx.createBiquadFilter();
  oscFilter.type = 'lowpass';
  oscFilter.frequency.setValueAtTime(3000, now);
  oscFilter.frequency.exponentialRampToValueAtTime(300, now + 0.12);
  
  const oscGain = ctx.createGain();
  oscGain.gain.setValueAtTime(0.7, now);
  oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
  
  osc.connect(oscFilter);
  oscFilter.connect(oscGain);
  oscGain.connect(masterGain);
  osc.start(now);
  osc.stop(now + 0.15);

  // 噪声层：白噪声短 burst
  const noiseBuffer = createNoiseBuffer(ctx, 0.15);
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuffer;
  
  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = 'highpass';
  noiseFilter.frequency.setValueAtTime(2000, now);
  
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.3, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
  
  noise.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(masterGain);
  noise.start(now);
  noise.stop(now + 0.15);

  // 金属共振：短 sine 在 ~1.5kHz
  const ring = ctx.createOscillator();
  ring.type = 'sine';
  ring.frequency.setValueAtTime(1400, now);
  ring.frequency.exponentialRampToValueAtTime(600, now + 0.1);
  
  const ringGain = ctx.createGain();
  ringGain.gain.setValueAtTime(0.25, now + 0.02);
  ringGain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
  
  ring.connect(ringGain);
  ringGain.connect(masterGain);
  ring.start(now + 0.02);
  ring.stop(now + 0.15);

  return { stop: () => { osc.stop(); noise.stop(); ring.stop(); } };
});

// ============================================================
// Hit — 低频闷响 + glitch 噪声 (0.2s)
// ============================================================
SYNTH_CACHE.set('hit', (ctx, dest) => {
  const now = ctx.currentTime;
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.5, now);
  masterGain.connect(dest);

  // 低频 thud：sine 80Hz 快速衰减
  const thud = ctx.createOscillator();
  thud.type = 'sine';
  thud.frequency.setValueAtTime(80, now);
  thud.frequency.exponentialRampToValueAtTime(40, now + 0.15);
  
  const thudGain = ctx.createGain();
  thudGain.gain.setValueAtTime(0.8, now);
  thudGain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
  
  thud.connect(thudGain);
  thudGain.connect(masterGain);
  thud.start(now);
  thud.stop(now + 0.2);

  // glitch 噪声：极短 noise burst
  const noiseBuf = createNoiseBuffer(ctx, 0.05);
  const glitch = ctx.createBufferSource();
  glitch.buffer = noiseBuf;
  
  const glitchFilter = ctx.createBiquadFilter();
  glitchFilter.type = 'bandpass';
  glitchFilter.frequency.setValueAtTime(3000, now);
  glitchFilter.Q.value = 5;
  
  const glitchGain = ctx.createGain();
  glitchGain.gain.setValueAtTime(0.4, now);
  glitchGain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
  
  glitch.connect(glitchFilter);
  glitchFilter.connect(glitchGain);
  glitchGain.connect(masterGain);
  glitch.start(now);
  glitch.stop(now + 0.08);

  return { stop: () => { thud.stop(); glitch.stop(); } };
});

// ============================================================
// Scan — 上升电子 sweep + 回声 ping (0.3s)
// ============================================================
SYNTH_CACHE.set('scan', (ctx, dest) => {
  const now = ctx.currentTime;
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.35, now);
  masterGain.connect(dest);

  // 上升 sweep：方波频率从 400 → 3000
  const sweep = ctx.createOscillator();
  sweep.type = 'square';
  sweep.frequency.setValueAtTime(400, now);
  sweep.frequency.exponentialRampToValueAtTime(3000, now + 0.25);
  
  const sweepFilter = ctx.createBiquadFilter();
  sweepFilter.type = 'bandpass';
  sweepFilter.frequency.setValueAtTime(400, now);
  sweepFilter.frequency.exponentialRampToValueAtTime(3000, now + 0.25);
  sweepFilter.Q.value = 2;
  
  const sweepGain = ctx.createGain();
  sweepGain.gain.setValueAtTime(0.5, now);
  sweepGain.gain.linearRampToValueAtTime(0.5, now + 0.2);
  sweepGain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
  
  sweep.connect(sweepFilter);
  sweepFilter.connect(sweepGain);
  sweepGain.connect(masterGain);
  sweep.start(now);
  sweep.stop(now + 0.3);

  // 回声 ping：在 sweep 结束后，高音 sine 短 ping
  const ping = ctx.createOscillator();
  ping.type = 'sine';
  ping.frequency.setValueAtTime(3000, now + 0.22);
  
  const pingGain = ctx.createGain();
  pingGain.gain.setValueAtTime(0.001, now);
  pingGain.gain.setValueAtTime(0.3, now + 0.22);
  pingGain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
  
  ping.connect(pingGain);
  pingGain.connect(masterGain);
  ping.start(now + 0.22);
  ping.stop(now + 0.35);

  return { stop: () => { sweep.stop(); ping.stop(); } };
});

// ============================================================
// Move — 低频机械伺服声 (0.1s)
// ============================================================
SYNTH_CACHE.set('move', (ctx, dest) => {
  const now = ctx.currentTime;
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.3, now);
  masterGain.connect(dest);

  // 低频伺服：sine 120Hz → 60Hz
  const servo = ctx.createOscillator();
  servo.type = 'sine';
  servo.frequency.setValueAtTime(120, now);
  servo.frequency.exponentialRampToValueAtTime(60, now + 0.08);
  
  const servoGain = ctx.createGain();
  servoGain.gain.setValueAtTime(0.6, now);
  servoGain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
  
  servo.connect(servoGain);
  servoGain.connect(masterGain);
  servo.start(now);
  servo.stop(now + 0.1);

  // 质地：极短 noise burst
  const noiseBuf = createNoiseBuffer(ctx, 0.03);
  const texture = ctx.createBufferSource();
  texture.buffer = noiseBuf;
  
  const texFilter = ctx.createBiquadFilter();
  texFilter.type = 'lowpass';
  texFilter.frequency.setValueAtTime(200, now);
  
  const texGain = ctx.createGain();
  texGain.gain.setValueAtTime(0.2, now);
  texGain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
  
  texture.connect(texFilter);
  texFilter.connect(texGain);
  texGain.connect(masterGain);
  texture.start(now);
  texture.stop(now + 0.06);

  return { stop: () => { servo.stop(); texture.stop(); } };
});

// ============================================================
// Destroy — glitch 中断 + 低频衰减 + 0.3s 静默 (0.5s)
// ============================================================
SYNTH_CACHE.set('destroy', (ctx, dest) => {
  const now = ctx.currentTime;
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.5, now);
  masterGain.connect(dest);

  // Phase 1: glitch 中断 (0-0.1s) — 3 个短噪声 burst
  for (let i = 0; i < 3; i++) {
    const t = now + i * 0.03;
    const glitchNoise = createNoiseBuffer(ctx, 0.02);
    const gn = ctx.createBufferSource();
    gn.buffer = glitchNoise;
    
    const gf = ctx.createBiquadFilter();
    gf.type = 'bandpass';
    gf.frequency.setValueAtTime(2000 - i * 500, t);
    gf.Q.value = 8;
    
    const gg = ctx.createGain();
    gg.gain.setValueAtTime(0.5 - i * 0.1, t);
    gg.gain.exponentialRampToValueAtTime(0.001, t + 0.02);
    
    gn.connect(gf);
    gf.connect(gg);
    gg.connect(masterGain);
    gn.start(t);
    gn.stop(t + 0.02);
  }

  // Phase 2: 低频衰减 (0.05-0.3s) — sine 轰降
  const boom = ctx.createOscillator();
  boom.type = 'sine';
  boom.frequency.setValueAtTime(60, now + 0.05);
  boom.frequency.exponentialRampToValueAtTime(20, now + 0.3);
  
  const boomGain = ctx.createGain();
  boomGain.gain.setValueAtTime(0.001, now);
  boomGain.gain.setValueAtTime(0.7, now + 0.05);
  boomGain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
  
  boom.connect(boomGain);
  boomGain.connect(masterGain);
  boom.start(now + 0.05);
  boom.stop(now + 0.5);

  return { stop: () => { boom.stop(); } };
});

// ============================================================
// Message — 数据 burst 脉冲 (0.15s)
// ============================================================
SYNTH_CACHE.set('message', (ctx, dest) => {
  const now = ctx.currentTime;
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.3, now);
  masterGain.connect(dest);

  // 3 个快速脉冲: 1200Hz → 1800Hz → 2400Hz
  const freqs = [1200, 1800, 2400];
  for (let i = 0; i < 3; i++) {
    const t = now + i * 0.04;
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(freqs[i], t);
    
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(freqs[i], t);
    filter.Q.value = 3;
    
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.4, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.03);
    
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(masterGain);
    osc.start(t);
    osc.stop(t + 0.04);
  }

  return { stop: () => {} };
});

// ============================================================
// UI Click — 方波脉冲 (0.05s)
// ============================================================
SYNTH_CACHE.set('ui_click', (ctx, dest) => {
  const now = ctx.currentTime;
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.25, now);
  masterGain.connect(dest);

  // 短促方波脉冲，2 个频率叠
  const osc1 = ctx.createOscillator();
  osc1.type = 'square';
  osc1.frequency.setValueAtTime(600, now);
  
  const osc1Gain = ctx.createGain();
  osc1Gain.gain.setValueAtTime(0.5, now);
  osc1Gain.gain.exponentialRampToValueAtTime(0.001, now + 0.04);
  
  osc1.connect(osc1Gain);
  osc1Gain.connect(masterGain);
  osc1.start(now);
  osc1.stop(now + 0.05);

  const osc2 = ctx.createOscillator();
  osc2.type = 'square';
  osc2.frequency.setValueAtTime(1200, now + 0.005);
  
  const osc2Gain = ctx.createGain();
  osc2Gain.gain.setValueAtTime(0.3, now + 0.005);
  osc2Gain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
  
  osc2.connect(osc2Gain);
  osc2Gain.connect(masterGain);
  osc2.start(now + 0.005);
  osc2.stop(now + 0.05);

  return { stop: () => { osc1.stop(); osc2.stop(); } };
});

// ============================================================
// Transition — 低频 whoosh（电流/数据质感）(0.5s)
// ============================================================
SYNTH_CACHE.set('transition', (ctx, dest) => {
  const now = ctx.currentTime;
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.4, now);
  masterGain.connect(dest);

  // 噪声 whoosh：bandpass 扫描 200Hz → 4000Hz → 200Hz
  const noiseBuf = createNoiseBuffer(ctx, 0.5);
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuf;
  
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(200, now);
  filter.frequency.exponentialRampToValueAtTime(4000, now + 0.25);
  filter.frequency.exponentialRampToValueAtTime(200, now + 0.5);
  filter.Q.value = 1.5;
  
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.001, now);
  noiseGain.gain.linearRampToValueAtTime(0.6, now + 0.1);
  noiseGain.gain.linearRampToValueAtTime(0.6, now + 0.35);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
  
  noise.connect(filter);
  filter.connect(noiseGain);
  noiseGain.connect(masterGain);
  noise.start(now);
  noise.stop(now + 0.5);

  // 低频脉动叠加
  const pulse = ctx.createOscillator();
  pulse.type = 'sine';
  pulse.frequency.setValueAtTime(80, now);
  pulse.frequency.linearRampToValueAtTime(80, now + 0.4);
  
  const pulseGain = ctx.createGain();
  pulseGain.gain.setValueAtTime(0.3, now + 0.05);
  pulseGain.gain.linearRampToValueAtTime(0.3, now + 0.3);
  pulseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
  
  pulse.connect(pulseGain);
  pulseGain.connect(masterGain);
  pulse.start(now + 0.05);
  pulse.stop(now + 0.5);

  return { stop: () => { noise.stop(); pulse.stop(); } };
});

// ============================================================
// Helpers
// ============================================================

function createNoiseBuffer(ctx: AudioContext, duration: number): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const length = Math.floor(sampleRate * duration);
  const buffer = ctx.createBuffer(1, length, sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) {
    data[i] = (Math.random() * 2 - 1) * 0.3;
  }
  return buffer;
}

// ============================================================
// Public API
// ============================================================

export function playSfx(ctx: AudioContext, dest: AudioNode, id: SfxId): SfxNode {
  const synth = SYNTH_CACHE.get(id);
  if (!synth) {
    console.warn(`[sfx] unknown sfx id: ${id}`);
    return { stop: () => {} };
  }
  return synth(ctx, dest);
}

/** 获取所有可用 SFX ID */
export function getSfxIds(): SfxId[] {
  return Array.from(SYNTH_CACHE.keys());
}
